import { Button } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import Table from "../../../../ui/components/table/table";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ListModel } from "../../../models/list-model";
import { PracticaList } from "../../../models/practica-list";
import { obrasocialServices } from "../../../services/obra-social.services";
import { practicaServices } from "../../../services/practica.services";
import { profesionalServices } from "../../../services/profesional.services";
import { getFormattedDate, getFormattedDecimal } from "../../../utils/common.utils";
import {
  initialFilter,
  queryParamsConfig,
} from "../../../utils/practica.utils";
import { initialProfesionalFilter } from "../../../utils/profesional.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import "./practicas-remito-admin.scss";

export default function PracticasRemitoAdmin() {
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [fetching, setFetching] = useState<boolean>(true);
  const [entities, setEntities] = useState<PracticaList[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [profesionalIdSelected, setProfesionalIdSelected] = useState<string>();
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();
  const [profesionales, setProfesionales] = useState<ListModel[]>([]);
  const [obrasSociales, setObrasSociales] = useState<ListModel[]>([]);
  const [obraSocialIdSelected, setObraSocialIdSelected] = useState<string>();
  const [queryParamsProfesional, setQueryParamsProfesional] = useState<any>();

  const getProfesionales = async (searchValue: string) => {
    setQueryParamsProfesional({
      ...initialProfesionalFilter,
      filter: searchValue,
    });
    profesionalServices
      .lookup(queryParamsProfesional)
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          items.push({
            value: element.id,
            label: element.value + " " + element.description,
          });
        });
        setProfesionales(items);
      })
      .catch((error) => {});
  };

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page && authInfo) {
      getPracticas();
    }
  }, [queryParams, authInfo]);

  useEffect(() => {
    getPracticas();
  }, [
    profesionalIdSelected,
    obraSocialIdSelected,
    monthSelected,
    yearSelected,
  ]);

  const getObrasSociales = async () => {
    obrasocialServices
      .lookup()
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          items.push({
            value: element.id,
            label: element.value,
          });
        });
        setObrasSociales(items);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    getObrasSociales();
    getProfesionales("");
  }, []);

  const getPracticas = async () => {
    if (authInfo) {
      let params = { ...queryParams };
      if (authInfo.role === "ADMIN" && profesionalIdSelected) {
        params = { ...params, profesionalId: profesionalIdSelected };
      }
      if (authInfo.role === "ADMIN" && obraSocialIdSelected) {
        params = { ...params, obraSocialId: obraSocialIdSelected };
      }
      setFetching(true);
      if (yearSelected && monthSelected !== undefined) {
        let fecha = new Date(yearSelected, monthSelected, 1);
        fecha.setUTCHours(0, 0, 0, 0);
        const fechaISO = fecha.toISOString();
        params = {
          ...params,
          mesAno: fechaISO,
          estado: "ENVIADA",
        };
        practicaServices
          .getList(params)
          .then((response) => {
            setFetching(false);
            setEntities(response.data.items);
            setTotalCount(response.data.totalItems);
          })
          .catch((error) => {});
      } else {
        setEntities([]);
        setTotalCount(0);
      }
    }
  };

  const columns: TableColumn<PracticaList>[] = [
    {
      name: "Profesional",
      selector: (row: any) => row.profesionalDescripcion,
      sortable: true,
      sortField: "profesional",
    },
    {
      name: "Obra Social",
      selector: (row) => row.obraSocialNombre,
      sortable: true,
      sortField: "obrasocial",
    },
    {
      name: "Fecha",
      selector: (row) => getFormattedDate(row.fecha.toString()),
      sortable: true,
      sortField: "fecha",
    },
    {
      name: "Paciente",
      selector: (row) => row.paciente,
      sortable: true,
      sortField: "paciente",
    },
    {
      name: "Nro. Validación Online",
      selector: (row) => row.nroValidacionOnline,
    },
    {
      name: "Código de Práctica",
      selector: (row) => row.practicaCodigo,
      wrap: true,
    },
    {
      name: "Descripcion",
      selector: (row) => row.practicaDescripcion,
    },
    {
      name: "Bono",
      selector: (row) => row.bono,
    },
    {
      name: "Cantidad",
      selector: (row) => row.cantidad,
    },
    {
      name: "Valor",
      selector: (row) => getFormattedDecimal(row.precio),
    },
    {
      name: "Valor Total",
      selector: (row) => getFormattedDecimal(row.precio * row.cantidad),
      wrap: true,
    },
    {
      name: "Observacion",
      selector: (row) => row.observacion,
      wrap: true,
    },
  ];

  const generarPdf = async (data: any) => {
    const doc = new jsPDF();

    const imgUrl = process.env.PUBLIC_URL + "/assets/img/listadoHeader.jpg";
    const imgData = await fetch(imgUrl)
      .then((res) => res.blob())
      .then((blob) => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      });

    const pageWidth = doc.internal.pageSize.getWidth();
    const imgWidth = 140; // Ancho de la imagen
    const imgHeight = 15; // Alto de la imagen

    const imgX = (pageWidth - imgWidth) / 2; // Centrar la imagen
    const imgY = 10; // Margen superior

    // Función para agregar encabezado
    const addHeader = (nombreObraSocial: string, periodo: string) => {
      doc.addImage(imgData, "JPEG", imgX, imgY, imgWidth, imgHeight);
      doc.setFontSize(16);
      doc.text("Listado de Prácticas", pageWidth / 2, imgY + imgHeight + 10, {
        align: "center",
      });
      doc.setFontSize(12);
      doc.text(`Obra Social: ${nombreObraSocial}`, 10, imgY + imgHeight + 32);
      doc.text(`Periodo: ${periodo}`, 10, imgY + imgHeight + 39);
    };

    // Agrupar por 'nombreObraSocial'
    const agrupadosPorObraSocial = data.reduce((result: any, item: any) => {
      const { nombreObraSocial } = item;
      if (!result[nombreObraSocial]) {
        result[nombreObraSocial] = [];
      }
      result[nombreObraSocial].push(item);
      return result;
    }, {});

    let isFirstPage = true;

    // Iterar sobre cada grupo de 'nombreObraSocial'
    for (const nombreObraSocial in agrupadosPorObraSocial) {
      const items = agrupadosPorObraSocial[nombreObraSocial];

      // Agregar nueva página si no es la primera
      if (!isFirstPage) {
        doc.addPage();
      }

      // Agregar encabezado con el nombre de la obra social, profesional y periodo
      addHeader(nombreObraSocial, items[0].periodo);

      // Generar la tabla de detalles de la práctica
      const tableData = items.flatMap((item: any) =>
        item.detalle.map((detalle: any) => [
          detalle.rowNumber,
          detalle.matriculaProf,
          detalle.nombreProfesional,
          detalle.cantidad,
          detalle.total.toFixed(2), // Asegurarse de mostrar el total como número decimal
        ])
      );

      // Calcular el total por obra social
      const totalPorObraSocial = items.reduce((total: number, item: any) => {
        return (
          total +
          item.detalle.reduce(
            (subtotal: number, detalle: any) => subtotal + detalle.total,
            0
          )
        );
      }, 0);

      // Configuración de la tabla
      autoTable(doc, {
        startY: imgY + imgHeight + 45, // Posicionar la tabla debajo del encabezado
        head: [["", "Matrícula", "Prestador", "Cant. Prácticas", "Total"]],
        body: tableData,
        theme: "grid",
        margin: { top: 10 },
        pageBreak: "auto", // Crea automáticamente nuevas páginas si es necesario
      });

      // Mostrar el total al final del grupo
      const tableEndY =
        (doc as any).lastAutoTable.finalY || imgY + imgHeight + 45;
      doc.setFontSize(12);
      doc.text(
        `Total Obra Social (${nombreObraSocial}): $ ${totalPorObraSocial.toFixed(
          2
        )}`,
        10,
        tableEndY + 10
      );

      isFirstPage = false;
    }

    // Descargar el PDF
    doc.save("reporte_por_os.pdf");
  };

  const buscarDataPDF = async () => {
    if (entities.length > 0 && authInfo) {
      if (yearSelected && monthSelected !== undefined) {
        let fecha = new Date(yearSelected, monthSelected, 1);
        fecha.setUTCHours(0, 0, 0, 0);
        const fechaISO = fecha.toISOString();
        practicaServices
          .getRemitoPorObraSocial(
            fechaISO,
            profesionalIdSelected,
            obraSocialIdSelected
          )
          .then((response: any) => {
            generarPdf(response.data);
          })
          .catch((error) => {});
      }
    }
  };

  return (
    <div className="practicas-remito-admin-container">
      <div className="row action-button">
        <DateInputFilter
          name="mesAno"
          hiddenDay={true}
          onChange={(value: Dayjs) => {
            if (value !== null) {
              setMonthSelected(value.get("month"));
              setYearSelected(value.get("year"));
            } else {
              setMonthSelected(undefined);
              setYearSelected(undefined);
            }
          }}
          maxDate={dayjs()}
        />
        {authInfo && profesionales.length > 0 && (
          <>
            <AutocompleteSelect
              name="profesionalId"
              label="Profesional"
              isMultiple={false}
              options={profesionales}
              onChange={(value) => {
                setProfesionalIdSelected(value);
              }}
            ></AutocompleteSelect>
            <AutocompleteSelect
              name="obraSocialId"
              label="Obra Social"
              isMultiple={false}
              options={obrasSociales}
              onChange={(value) => {
                setObraSocialIdSelected(value);
              }}
            ></AutocompleteSelect>
          </>
        )}
        <Button
          className="button"
          variant="text"
          onClick={() => buscarDataPDF()}
          disabled={entities.length === 0}
        >
          Exportar
        </Button>
      </div>
      <div className="row">
        <div className="practicas-table">
          {entities.length > 0 && <h2>Prácticas con estado Enviada</h2>}
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              pointerOnHover: true,
              highlightOnHover: true,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
          ></Table>
        </div>
      </div>
    </div>
  );
}
